import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
});

API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ── Auth ──────────────────────────────────────────────────────
// referralCode is forwarded if present in localStorage (set on landing from ?ref=...).
// Backend silently ignores unknown / blank codes.
const _refCode = () => {
  try { return localStorage.getItem('ck_referral_code') || ''; } catch (e) { return ''; }
};

export const authAPI = {
  googleLogin: (credential) => API.post('/auth/google', { credential, referralCode: _refCode() }),
  emailLogin:  (email, password) => API.post('/auth/login', { email, password }),
  register:    (name, email, password) => API.post('/auth/register', { name, email, password, referralCode: _refCode() }),
  me:          () => API.get('/auth/me'),
  verifyEmail:               (token) => API.post('/auth/verify-email', { token }),
  resendVerification:        ()      => API.post('/auth/resend-verification'),
  resendVerificationByEmail: (email) => API.post('/auth/resend-verification-public', { email }),
  forgotPassword:            (email) => API.post('/auth/forgot-password', { email }),
  validateResetToken:        (token) => API.post('/auth/validate-reset-token', { token }),
  resetPassword:             (token, password) => API.post('/auth/reset-password', { token, password }),
  /**
   * Soft-delete the current user's account. Server requires:
   *   - confirmEmail: must match user's current email (case-insensitive)
   *   - reason: optional product-insight string
   * After success, clear localStorage and redirect to landing.
   */
  deleteAccount:             (data)  => API.delete('/auth/account', { data }),
  requestRestore:            (email) => API.post('/auth/request-restore', { email }),
  restoreAccount:            (token) => API.post('/auth/restore', { token }),
};

// ── Referrals ─────────────────────────────────────────────────
export const referralAPI = {
  me: () => API.get('/referrals/me'),
};

// ── Brand Deals ───────────────────────────────────────────────
export const dealAPI = {
  getAll:    ()       => API.get('/deals'),
  create:    (data)   => API.post('/deals', data),
  update:    (id, data) => API.put(`/deals/${id}`, data),
  delete:    (id)     => API.delete(`/deals/${id}`),
  updateStatus: (id, status) => API.patch(`/deals/${id}/status`, { status }),
};

// ── Content Calendar ──────────────────────────────────────────
export const contentAPI = {
  getAll:    ()       => API.get('/content'),
  create:    (data)   => API.post('/content', data),
  update:    (id, data) => API.put(`/content/${id}`, data),
  delete:    (id)     => API.delete(`/content/${id}`),
  updateStatus: (id, status) => API.patch(`/content/${id}/status`, { status }),
};

// ── Idea Vault ────────────────────────────────────────────────
export const ideaAPI = {
  getAll:  ()     => API.get('/ideas'),
  create:  (data) => API.post('/ideas', data),
  update:  (id, data) => API.put(`/ideas/${id}`, data),
  delete:  (id)   => API.delete(`/ideas/${id}`),
  aiSuggest: (niche) => API.post('/ideas/suggest', { niche }),
};

// ── Payments (Razorpay Subscriptions) ─────────────────────────
export const paymentAPI = {
  createSubscription: (planKey) => API.post('/payments/create-subscription', { planKey }),
  verifySubscription: (data)    => API.post('/payments/verify-subscription', data),
  cancelSubscription: ()        => API.post('/payments/cancel-subscription'),
  getSubscription:    ()        => API.get('/payments/subscription'),
};

// ── Invoices ───────────────────────────────────────────────────
export const invoiceAPI = {
  getAll:       ()           => API.get('/invoices'),
  getById:      (id)         => API.get(`/invoices/${id}`),
  create:       (data)       => API.post('/invoices', data),
  update:       (id, data)   => API.put(`/invoices/${id}`, data),
  delete:       (id)         => API.delete(`/invoices/${id}`),
  updateStatus: (id, status) => API.patch(`/invoices/${id}/status`, { status }),
  downloadPdf:  (id)         => API.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// ── Profile ────────────────────────────────────────────────────
export const profileAPI = {
  update: (data) => API.put('/auth/profile', data),
};

// ── Media Kit ──────────────────────────────────────────────────
export const mediaKitAPI = {
  get:         ()     => API.get('/mediakit'),
  save:        (data) => API.post('/mediakit', data),
  downloadPdf: ()     => API.get('/mediakit/pdf', { responseType: 'blob' }),
};

// ── Rate Card ──────────────────────────────────────────────────
export const rateCardAPI = {
  get:         ()     => API.get('/ratecard'),
  save:        (data) => API.post('/ratecard', data),
  downloadPdf: ()     => API.get('/ratecard/pdf', { responseType: 'blob' }),
};

// ── Contracts ──────────────────────────────────────────────────
export const contractAPI = {
  getAll:       ()          => API.get('/contracts'),
  getById:      (id)        => API.get(`/contracts/${id}`),
  create:       (data)      => API.post('/contracts', data),
  update:       (id, data)  => API.put(`/contracts/${id}`, data),
  delete:       (id)        => API.delete(`/contracts/${id}`),
  updateStatus: (id, status)=> API.patch(`/contracts/${id}/status`, { status }),
  downloadPdf:  (id)        => API.get(`/contracts/${id}/pdf`, { responseType: 'blob' }),
};

// ── Analytics ──────────────────────────────────────────────────
export const analyticsAPI = {
  getAll:      ()       => API.get('/analytics'),
  getSummary:  ()       => API.get('/analytics/summary'),
  getByPlatform:(p)     => API.get(`/analytics/platform/${p}`),
  save:        (data)   => API.post('/analytics', data),
  delete:      (id)     => API.delete(`/analytics/${id}`),
};

// ── Payments Tracker ───────────────────────────────────────────
export const paymentsTrackerAPI = {
  getAll:       ()          => API.get('/payments-tracker'),
  getSummary:   ()          => API.get('/payments-tracker/summary'),
  create:       (data)      => API.post('/payments-tracker', data),
  update:       (id, data)  => API.put(`/payments-tracker/${id}`, data),
  delete:       (id)        => API.delete(`/payments-tracker/${id}`),
  markReceived: (id, data)  => API.patch(`/payments-tracker/${id}/received`, data),
};

// ── Content Repurpose ──────────────────────────────────────────
export const repurposeAPI = {
  getAll:   ()     => API.get('/repurpose'),
  generate: (data) => API.post('/repurpose/generate', data),
  delete:   (id)   => API.delete(`/repurpose/${id}`),
};

// ── Trending Topics ────────────────────────────────────────────
export const trendingAPI = {
  getAll:       ()                    => API.get('/trending'),
  suggest:      (niche, platform)     => API.get('/trending/suggest', { params: { niche, platform } }),
  save:         (data)                => API.post('/trending', data),
  update:       (id, data)            => API.put(`/trending/${id}`, data),
  updateStatus: (id, data)            => API.patch(`/trending/${id}/status`, data),
  delete:       (id)                  => API.delete(`/trending/${id}`),
};

// ── Caption + Hashtag Generator ───────────────────────────────
export const captionAPI = {
  getAll:   ()     => API.get('/captions'),
  generate: (data) => API.post('/captions/generate', data),
  delete:   (id)   => API.delete(`/captions/${id}`),
};

// ── Bio / Link-in-Bio ──────────────────────────────────────────
export const bioAPI = {
  getMe:        ()          => API.get('/bio/me'),
  saveProfile:  (data)      => API.post('/bio/profile', data),
  slugCheck:    (slug)      => API.get('/bio/slug-check', { params: { slug } }),
  addLink:      (data)      => API.post('/bio/links', data),
  updateLink:   (id, data)  => API.put(`/bio/links/${id}`, data),
  toggleLink:   (id)        => API.patch(`/bio/links/${id}/toggle`),
  deleteLink:   (id)        => API.delete(`/bio/links/${id}`),
};

// ── Collab Manager ─────────────────────────────────────────────
export const collabAPI = {
  getAll:       ()          => API.get('/collabs'),
  getSummary:   ()          => API.get('/collabs/summary'),
  getById:      (id)        => API.get(`/collabs/${id}`),
  create:       (data)      => API.post('/collabs', data),
  update:       (id, data)  => API.put(`/collabs/${id}`, data),
  updateStatus: (id, data)  => API.patch(`/collabs/${id}/status`, data),
  delete:       (id)        => API.delete(`/collabs/${id}`),
};

// ── Brand Outreach ─────────────────────────────────────────────
export const outreachAPI = {
  getAll:       ()          => API.get('/outreach'),
  getById:      (id)        => API.get(`/outreach/${id}`),
  generate:     (data)      => API.post('/outreach/generate', data),
  update:       (id, data)  => API.put(`/outreach/${id}`, data),
  updateStatus: (id, data)  => API.patch(`/outreach/${id}/status`, data),
  delete:       (id)        => API.delete(`/outreach/${id}`),
};

// ── Support Tickets ────────────────────────────────────────────
export const supportAPI = {
  create:       (data)      => API.post('/support/tickets', data),
  getAll:       ()          => API.get('/support/tickets'),
  updateStatus: (id, status)=> API.patch(`/support/tickets/${id}/status`, { status }),
};

// ── Priya Chatbot ──────────────────────────────────────────────
// Public endpoint — works for both authenticated and anonymous (landing) users.
export const chatAPI = {
  ask: (messages, context) =>
    API.post('/public/chat/priya', { messages, context }),
};
